import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import {
  setAccountToDisplay,
  useAccountToDisplaySelector,
  useManagedAccountsSelector
} from "../../redux/actions/user";
import KC_Heading3 from "../../designsystem/typography/KC-Heading/KC_Heading3";
import type ManagedAccount from "@tootsweet/model/entities/ManagedAccount";
import { getAccountDisplayName } from "./getAccountDisplayName";
import "./styles.scss";

const ALL_ACCOUNTS_EMAIL = "ALL_ACCOUNTS";

interface ManagedAccountDropdownProps {
  showAllOption?: boolean;
}

export const ManagedAccountDropdown: React.FC<ManagedAccountDropdownProps> = (
  props
) => {
  const showAllOption =
    props.showAllOption === undefined ? false : props.showAllOption;
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const managedAccounts = useManagedAccountsSelector();
  const { accountToDisplay, accountToDisplayIndex } =
    useAccountToDisplaySelector();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const accountsWithAll = useMemo(() => {
    if (!showAllOption || managedAccounts.length <= 1) {
      return managedAccounts;
    }

    // const allAccount: ManagedAccount = {
    //   email: ALL_ACCOUNTS_EMAIL,
    //   placeName: tt("all_accounts"),
    //   isAll: true,
    //   isOnboarded: true,
    //   isJetpack: true,
    //   avgBasket: 0,
    //   currency: DEFAULT_CURRENCY,
    //   dCurrency: DEFAULT_CURRENCY,
    //   curSubUnits: DEFAULT_CURRENCY_SUB_UNITS,
    //   bizContrib: DEFAULT_CURRENCY_SUB_UNITS
    // };

    // return [allAccount, ...managedAccounts];
    return [...managedAccounts];
  }, [managedAccounts, showAllOption]);

  const handleAccountChange = (account: ManagedAccount) => {
    // TODO EGH: delete this, should be handled by URL change
    dispatch(setAccountToDisplay(account));
    setIsOpen(false);

    // Update URL with new trackingID if the current route supports it
    if (account.trackingID && location.pathname.includes("/")) {
      const currentPath = location.pathname;
      const currentTrackingID = accountToDisplay?.trackingID;

      // If the current path has a trackingID, replace it
      if (currentTrackingID && currentPath.includes(currentTrackingID)) {
        const newPath = currentPath.replace(
          currentTrackingID,
          account.trackingID
        );
        history.replace(newPath);
      }
    }
  };

  const selectedAccountName = useMemo(() => {
    return getAccountDisplayName(accountToDisplay, accountToDisplayIndex);
  }, [accountToDisplay]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  if (managedAccounts.length <= 1 && !showAllOption) {
    return null;
  }

  return (
    <div
      ref={dropdownRef}
      className="p-2 d-flex flex-row managed-account-dropdown"
    >
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="managed-account-dropdown__trigger"
      >
        <KC_Heading3 text={selectedAccountName} />
        <span
          className={`managed-account-dropdown__arrow ${
            isOpen
              ? "managed-account-dropdown__arrow--open"
              : "managed-account-dropdown__arrow--closed"
          }`}
        >
          ▼
        </span>
      </div>
      {isOpen && (
        <div className="managed-account-dropdown__menu">
          {accountsWithAll.map((account, idx) => {
            if (account.businessUser?.hide) {
              return null;
            }
            return (
              <div
                key={account.isAll ? "__all__" : account.email}
                onClick={() => handleAccountChange(account)}
                className="managed-account-dropdown__item"
              >
                <div className="d-flex flex-column">
                  <span>{getAccountDisplayName(account, idx)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
